/**
 * AchievementsView - Vista de logros y badges
 *
 * Diseñado para neurodivergencia:
 * - Celebra el progreso sin presion
 * - Muestra progreso hacia logros proximos
 * - Lenguaje positivo y motivador
 * - Sin comparaciones ni rankings
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Lock, Sparkles, ChevronRight } from 'lucide-react';
import { cn } from '@/core/utils';
import {
  ACHIEVEMENTS,
  loadAchievementState,
  isAchievementUnlocked,
  getAchievementProgress,
  type Achievement,
  type AchievementCategory,
  type UserAchievementState,
} from '@/services/achievements';

const CATEGORY_INFO: Record<AchievementCategory, { label: string; icon: string; color: string }> = {
  streak: { label: 'Rachas', icon: '🔥', color: 'text-orange-400' },
  completion: { label: 'Completados', icon: '✅', color: 'text-emerald-400' },
  resilience: { label: 'Resiliencia', icon: '💪', color: 'text-violet-400' },
  exploration: { label: 'Exploración', icon: '🧭', color: 'text-blue-400' },
  milestone: { label: 'Hitos', icon: '🏆', color: 'text-amber-400' },
};

const TIER_COLORS = {
  bronze: 'from-orange-700 to-orange-900 border-orange-600',
  silver: 'from-slate-400 to-slate-600 border-slate-400',
  gold: 'from-yellow-500 to-yellow-700 border-yellow-500',
  platinum: 'from-cyan-400 to-cyan-600 border-cyan-400',
};

export function AchievementsView() {
  const [state, setState] = useState<UserAchievementState | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<AchievementCategory | 'all'>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    setState(loadAchievementState());
  }, []);

  if (!state) return null;

  const categories: (AchievementCategory | 'all')[] = [
    'all',
    'streak',
    'completion',
    'resilience',
    'exploration',
  ];

  const filteredAchievements =
    selectedCategory === 'all'
      ? ACHIEVEMENTS
      : ACHIEVEMENTS.filter((a) => a.category === selectedCategory);

  const unlockedCount = state.unlockedIds.length;
  const totalCount = ACHIEVEMENTS.length;
  const progressPercentage = Math.round((unlockedCount / totalCount) * 100);

  // Encontrar el proximo logro mas cercano
  const nextAchievement = ACHIEVEMENTS.find(
    (a) => !isAchievementUnlocked(a, state) && getAchievementProgress(a, state) > 0
  );

  return (
    <div className="space-y-6">
      {/* Header con progreso general */}
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-5 border border-slate-700">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-amber-500/20 rounded-xl">
            <Trophy className="w-8 h-8 text-amber-400" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-slate-100">Tus logros</h2>
            <p className="text-sm text-slate-400">
              {unlockedCount} de {totalCount} desbloqueados
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-amber-400">{progressPercentage}%</div>
          </div>
        </div>

        {/* Barra de progreso */}
        <div className="mt-4 h-2 bg-slate-700 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-amber-500 to-amber-400 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progressPercentage}%` }}
            transition={{ duration: 0.5, delay: 0.2 }}
          />
        </div>

        {/* Proximo logro */}
        {nextAchievement && (
          <div className="mt-4 p-3 bg-slate-800/50 rounded-lg">
            <p className="text-xs text-slate-500 mb-1">Próximo logro</p>
            <div className="flex items-center gap-2">
              <span className="text-lg">{nextAchievement.icon}</span>
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-200">{nextAchievement.title}</p>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full"
                      style={{ width: `${getAchievementProgress(nextAchievement, state)}%` }}
                    />
                  </div>
                  <span className="text-xs text-slate-400">
                    {getAchievementProgress(nextAchievement, state)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Filtros por categoria */}
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
        {categories.map((cat) => {
          const isAll = cat === 'all';
          const info = isAll ? null : CATEGORY_INFO[cat];
          return (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors',
                selectedCategory === cat
                  ? 'bg-slate-700 text-slate-100'
                  : 'bg-slate-800/50 text-slate-400 hover:text-slate-200'
              )}
            >
              {isAll ? '✨' : info?.icon}
              <span>{isAll ? 'Todos' : info?.label}</span>
            </button>
          );
        })}
      </div>

      {/* Lista de logros */}
      <div className="space-y-3">
        <AnimatePresence mode="popLayout">
          {filteredAchievements.map((achievement, index) => (
            <AchievementCard
              key={achievement.id}
              achievement={achievement}
              state={state}
              index={index}
              isExpanded={expandedId === achievement.id}
              onToggle={() =>
                setExpandedId(expandedId === achievement.id ? null : achievement.id)
              }
            />
          ))}
        </AnimatePresence>
      </div>

      {/* Mensaje motivacional */}
      <div className="text-center py-4">
        <p className="text-sm text-slate-500">
          {unlockedCount === 0
            ? 'Cada pequeno paso cuenta. Los logros llegaran con la consistencia.'
            : unlockedCount < 5
            ? 'Buen comienzo. Sigue tu propio ritmo.'
            : unlockedCount < 10
            ? 'Impresionante progreso. La constancia da frutos.'
            : 'Eres un ejemplo de perseverancia.'}
        </p>
      </div>
    </div>
  );
}

interface AchievementCardProps {
  achievement: Achievement;
  state: UserAchievementState;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
}

function AchievementCard({
  achievement,
  state,
  index,
  isExpanded,
  onToggle,
}: AchievementCardProps) {
  const unlocked = isAchievementUnlocked(achievement, state);
  const progress = getAchievementProgress(achievement, state);
  const categoryInfo = CATEGORY_INFO[achievement.category];

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ delay: index * 0.05 }}
    >
      <button
        onClick={onToggle}
        className={cn(
          'w-full text-left rounded-xl p-4 transition-all',
          unlocked
            ? 'bg-gradient-to-br from-slate-800 to-slate-850 border border-slate-700 hover:border-slate-600'
            : 'bg-slate-900/50 border border-slate-800 opacity-70 hover:opacity-90'
        )}
      >
        <div className="flex items-start gap-3">
          {/* Icono con tier */}
          <div
            className={cn(
              'relative w-12 h-12 rounded-xl flex items-center justify-center text-2xl',
              unlocked
                ? `bg-gradient-to-br ${TIER_COLORS[achievement.tier]} border`
                : 'bg-slate-800 border border-slate-700'
            )}
          >
            {unlocked ? (
              achievement.icon
            ) : (
              <Lock className="w-5 h-5 text-slate-600" />
            )}
            {unlocked && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-1 -right-1"
              >
                <Sparkles className="w-4 h-4 text-amber-400" />
              </motion.div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3
                className={cn(
                  'font-medium truncate',
                  unlocked ? 'text-slate-100' : 'text-slate-400'
                )}
              >
                {achievement.title}
              </h3>
              <span className={cn('text-xs', categoryInfo.color)}>
                {categoryInfo.icon}
              </span>
            </div>
            <p className="text-sm text-slate-500 line-clamp-1 mt-0.5">
              {achievement.description}
            </p>

            {/* Progreso si no esta desbloqueado */}
            {!unlocked && progress > 0 && (
              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <span className="text-xs text-slate-500">{progress}%</span>
              </div>
            )}
          </div>

          {/* Flecha */}
          <ChevronRight
            className={cn(
              'w-5 h-5 text-slate-600 transition-transform',
              isExpanded && 'rotate-90'
            )}
          />
        </div>

        {/* Detalles expandidos */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="mt-4 pt-4 border-t border-slate-800">
                <p className="text-sm text-slate-300">
                  {unlocked
                    ? achievement.celebrationMessage
                    : achievement.encouragement}
                </p>

                {/* Tier badge */}
                <div className="mt-3 flex items-center gap-2">
                  <span
                    className={cn(
                      'px-2 py-0.5 rounded-full text-xs font-medium capitalize',
                      achievement.tier === 'bronze' && 'bg-orange-900/50 text-orange-400',
                      achievement.tier === 'silver' && 'bg-slate-600/50 text-slate-300',
                      achievement.tier === 'gold' && 'bg-yellow-900/50 text-yellow-400',
                      achievement.tier === 'platinum' && 'bg-cyan-900/50 text-cyan-400'
                    )}
                  >
                    {achievement.tier}
                  </span>
                  <span className="text-xs text-slate-600">
                    {unlocked ? 'Desbloqueado' : 'Bloqueado'}
                  </span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </button>
    </motion.div>
  );
}
